/* eslint-disable @eslint-community/eslint-comments/disable-enable-pair -- Required for compatibility with project linting rules */

"use client";

// libraries/components/src/components/qualifier/form/index.tsx

import {
  useForm,
  useController,
  type UseFormReturn,
  type FieldPath,
  type RegisterOptions,
} from "react-hook-form";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ReactElement,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@jetblue/kit/button";
import { InputText as ClientInput } from "@jetblue/kit/input-text";

import { useManageTripsConfig } from "@jb-manage-trips/runtime-config";
import {
  useHealthMutation,
  type HealthResponse,
} from "@jb-manage-trips/api/mutations/health";
import type { QualifierHttpError } from "../services/qualifier.client";
import type { MybQualifierRequestBody as QualifierRequestBody } from "@jb-manage-trips/types/pnr";
import { createQualifierService } from "../services/qualifier.client";

import {
  getDurationMs,
  getErrorTelemetry,
  getTelemetryStart,
  logManageTripsEvent,
  type ManageTripsTelemetryOperation,
  type ManageTripsTelemetryResultRoute,
} from "@jb-manage-trips/fe-logger/manage-trips-events";

/* ---------- helpers (module scope to satisfy lint rules) ---------- */

function booleanStringCheck(
  value: boolean | string | undefined | null,
): boolean {
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }

  return Boolean(value);
}

function isLikelyEmail(value: string): boolean {
  const trimmed = value.trim();
  const at = trimmed.indexOf("@");
  const lastAt = trimmed.lastIndexOf("@");
  const dot = trimmed.lastIndexOf(".");
  return at > 0 && at === lastAt && dot >= at + 2 && dot < trimmed.length - 1;
}

function isValidIntlPhone(raw: string): boolean {
  if (!raw.startsWith("+")) {
    return false;
  }
  const digits = raw.slice(1).replaceAll(" ", "");
  if (!/^\d+$/.test(digits)) {
    return false;
  }
  const length = digits.length;
  return length >= 7 && length <= 15;
}

function isValidUs10(s: string): boolean {
  return /^\d{10}$/.test(s);
}

/* ------------------------------------------------------------------ */

type QualifierLabels = {
  email?: {
    description?: string;
    label?: string;
    validationLabels?: {
      invalid?: string;
      required: string;
    }[];
  };
  dateOfBirth?: {
    description?: string;
    label?: string;
    monthLabel?: string;
    dayLabel?: string;
    yearLabel?: string;
    placeholderTextMonth?: string;
    placeholderTextDay?: string;
    placeholderTextYear?: string;
    validationLabels?: {
      invalid: string;
      required: string;
    }[];
  };
  phone?: {
    description?: string;
    label?: string;
    validationMessages?: {
      invalid?: string;
      required?: string;
    }[];
  }[];
  submitCTA?: { label: string }[];
  skipButton?: { label: string }[];
  formValidationMessages?: {
    serviceError?: string;
    noMatchFound?: string;
    emailFailedToUpdate?: string;
    phoneFailedToUpdate?: string;
  }[];
};

type FormValues = {
  day?: string;
  email?: string;
  month?: string;
  phone?: string;
  year?: string;
};

const defaultValues: FormValues = {
  day: "",
  email: "",
  month: "",
  phone: "",
  year: "",
};

export default function QualifierFormClient(
  properties: Readonly<{ labels: Readonly<QualifierLabels> }>,
): ReactElement | null {
  const { labels } = properties;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments -- prefer this pattern
  const form: UseFormReturn<FormValues> = useForm<FormValues>({
    defaultValues,
    mode: "onSubmit",
  });
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = form;

  const useField = <TName extends FieldPath<FormValues>>(
    name: TName,
    rules?: RegisterOptions<FormValues, TName>,
  ) =>
    useController({
      control,
      name,
      ...(rules ? { rules } : {}),
    });

  const router = useRouter();
  const search = useSearchParams();

  const manageTripsConfig = useManageTripsConfig();
  const svc = useMemo(
    () => createQualifierService(manageTripsConfig),
    [manageTripsConfig],
  );

  const healthMutation = useHealthMutation();

  const platformOS = useMemo<"iOS" | "Android" | "Other">(() => {
    if (typeof navigator === "undefined") {
      return "Other";
    }
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) {
      return "iOS";
    }
    if (ua.includes("Android")) {
      return "Android";
    }
    return "Other";
  }, []);

  const emailExistParameter = search.get("emailExist");
  const phoneExistParameter = search.get("phoneNumberExist");
  const lastNameParameter = search.get("lastName");
  const pnrParameter = search.get("pnr");

  const guardInvalid =
    emailExistParameter === null ||
    phoneExistParameter === null ||
    !lastNameParameter ||
    !pnrParameter;

  const emailExist = booleanStringCheck(emailExistParameter);
  const phoneNumberExist = booleanStringCheck(phoneExistParameter);
  const isFormWithPhoneOnly = !phoneNumberExist && emailExist;
  const requireDob = !emailExist;

  useEffect(() => {
    logManageTripsEvent({
      emailExists: emailExist,
      eventName: guardInvalid
        ? "qualifier_flow_failed"
        : "qualifier_view_loaded",
      feature: "qualifier",
      flow: "qualifier",
      operation: guardInvalid ? "route_decision" : "qualifier_update",
      phoneExists: phoneNumberExist,
      resultRoute: guardInvalid ? "none" : undefined,
      status: guardInvalid ? "failure" : "started",
    });
  }, [emailExist, guardInvalid, phoneNumberExist]);

  const emailMsgs = {
    invalid:
      labels.email?.validationLabels?.[0]?.invalid ??
      "Must be a real email address",
    required:
      labels.email?.validationLabels?.[0]?.required ?? "Enter a traveler email",
  };
  const dobMsgs = {
    invalid:
      labels.dateOfBirth?.validationLabels?.[0]?.invalid ??
      "Enter a valid past date (MM DD YYYY)",
    required:
      labels.dateOfBirth?.validationLabels?.[0]?.required ??
      "Date of birth is required",
  };
  const phoneMsgs = {
    invalid:
      labels.phone?.[0]?.validationMessages?.[0]?.invalid ??
      "Invalid phone number",
    required:
      labels.phone?.[0]?.validationMessages?.[0]?.required ??
      "Phone number is required",
  };

  const { field: emailField, fieldState: emailState } = useField("email", {
    ...(emailExist
      ? {}
      : {
          required: emailMsgs.required,
          validate: (v?: string) => {
            if (!v) {
              return emailMsgs.required;
            }
            return isLikelyEmail(v) || emailMsgs.invalid;
          },
        }),
  });

  const { field: monthField, fieldState: monthState } = useField("month", {
    ...(requireDob
      ? {
          required: dobMsgs.required,
          validate: (v?: string) => {
            const n = Number(v);
            if (!v) {
              return dobMsgs.required;
            }
            if (!Number.isFinite(n)) {
              return dobMsgs.invalid;
            }
            if (n < 1 || n > 12) {
              return dobMsgs.invalid;
            }
            return true;
          },
        }
      : {}),
  });

  const { field: dayField, fieldState: dayState } = useField("day", {
    ...(requireDob
      ? {
          required: dobMsgs.required,
          validate: (v?: string) => {
            const n = Number(v);
            if (!v) {
              return dobMsgs.required;
            }
            if (!Number.isFinite(n)) {
              return dobMsgs.invalid;
            }
            if (n < 1 || n > 31) {
              return dobMsgs.invalid;
            }
            return true;
          },
        }
      : {}),
  });

  const { field: yearField, fieldState: yearState } = useField("year", {
    ...(requireDob
      ? {
          required: dobMsgs.required,
          validate: (v?: string) => {
            const n = Number(v);
            const now = new Date().getFullYear();
            if (!v) {
              return dobMsgs.required;
            }
            if (!Number.isFinite(n)) {
              return dobMsgs.invalid;
            }
            if (v.trim().length < 4) {
              return dobMsgs.invalid;
            }
            if (n < 1900 || n > now) {
              return dobMsgs.invalid;
            }
            return true;
          },
        }
      : {}),
  });

  const { field: phoneField, fieldState: phoneState } = useField("phone", {
    ...(phoneNumberExist
      ? {}
      : {
          ...(isFormWithPhoneOnly ? { required: phoneMsgs.required } : {}),
          validate: (v?: string) => {
            if (!v && !isFormWithPhoneOnly) {
              return true;
            }
            if (!v && isFormWithPhoneOnly) {
              return phoneMsgs.required;
            }
            const cleaned = (v ?? "").replaceAll(/\s/g, "");
            return (
              isValidUs10(cleaned) ||
              isValidIntlPhone(v ?? "") ||
              phoneMsgs.invalid
            );
          },
        }),
  });

  const [hasRedirected, setHasRedirected] = useState(false);
  useEffect(() => {
    if (hasRedirected) {
      return;
    }
    if (guardInvalid) {
      setHasRedirected(true);
      router.replace("/");
    }
  }, [guardInvalid, hasRedirected, router]);

  const onDobBlur = useCallback(
    (which: "month" | "day" | "year") => {
      let value = "";
      if (which === "month") {
        value = monthField.value ?? "";
      } else if (which === "day") {
        value = dayField.value ?? "";
      } else {
        value = yearField.value ?? "";
      }

      if (value === "") {
        return;
      }

      if (which === "month" || which === "day") {
        const n = Number(value);
        if (Number.isFinite(n) && value.length === 1) {
          const padded = value.padStart(2, "0");
          if (which === "month") {
            monthField.onChange(padded);
          } else {
            dayField.onChange(padded);
          }
        }
      } else {
        yearField.onChange(value.trim());
      }
    },
    [
      dayField.onChange,
      dayField.value,
      monthField.onChange,
      monthField.value,
      yearField.onChange,
      yearField.value,
    ],
  );

  const onPhoneBlur = useCallback(() => {
    const v = phoneField.value ?? "";
    const cleaned = v.replaceAll(/\s/g, "");
    phoneField.onChange(cleaned);
  }, [phoneField]);

  const [serverError, setServerError] = useState<string | null>(null);
  const [isPendingTransition] = useTransition();
  const abortReference = useRef<AbortController | null>(null);
  const newAbortSignal = useCallback((): AbortSignal => {
    if (abortReference.current) {
      abortReference.current.abort();
    }
    const c = new AbortController();
    abortReference.current = c;
    return c.signal;
  }, []);

  const message = labels.formValidationMessages?.[0] ?? {};
  const messageForCode = (code?: string) => {
    switch (code) {
      case "MYB-QUALIFIER-PNR-NOT-FOUND-40000":
      case "MYB-QUALIFIER-INVALID-DOB-OR-LASTNAME-40001":
      case "MYB-QUALIFIER-INVALID-LASTNAME-40007":
      case "MYB-QUALIFIER-INVALID-DOB-40008": {
        return (
          message.noMatchFound ??
          "The date of birth does not match a traveler on this trip."
        );
      }
      case "MYB-QUALIFIER-INVALID-EMAIL-VALIDATION-ERROR": {
        return (
          message.emailFailedToUpdate ??
          "There was a problem updating email. Check and try again."
        );
      }
      case "MYB-QUALIFIER-INVALID-PHONE-VALIDATION-ERROR": {
        return (
          message.phoneFailedToUpdate ??
          "There was a problem updating phone. Check and try again."
        );
      }
      default: {
        return (
          message.serviceError ??
          "It's not you, it's us. Please refresh your browser or try again later."
        );
      }
    }
  };

  const afterQualifierFlow = useCallback(
    async ({
      completionStatus,
      telemetryStart,
    }: {
      completionStatus: "success" | "skipped";
      telemetryStart: number;
    }) => {
      const lastName = lastNameParameter ?? "";
      const pnr = pnrParameter ?? "";

      let failedOperation: ManageTripsTelemetryOperation = "pnr_health_check";
      let resultRoute: ManageTripsTelemetryResultRoute | undefined;
      let pnrState: string | undefined;

      try {
        const effectiveHealth: HealthResponse =
          await healthMutation.mutateAsync({
            lastName,
            b6PNRRecordLocator: pnr,
          });

        pnrState = effectiveHealth.pnrState;

        const localHealthCheckUrl = Boolean(
          process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL,
        );

        resultRoute =
          effectiveHealth.pnrState === "normal" && !localHealthCheckUrl
            ? "sabre-dx"
            : "itinerary";

        failedOperation = "route_decision";

        logManageTripsEvent({
          durationMs: getDurationMs(telemetryStart),
          emailExists: emailExist,
          eventName: "qualifier_flow_completed",
          feature: "qualifier",
          flow: "qualifier",
          operation: "route_decision",
          phoneExists: phoneNumberExist,
          pnrState,
          resultRoute,
          status: completionStatus,
        });

        if (resultRoute === "sabre-dx") {
          const queryParameters = Object.fromEntries(search.entries());
          const url = svc.buildSabreDxUrl(
            lastName,
            pnr,
            queryParameters,
            platformOS,
          );

          globalThis.location.assign(url);
          return;
        }

        const itinerarySearch = new URLSearchParams({
          lastName,
          pnr,
          locale: search.get("locale") ?? "",
        });

        router.push(`${svc.routes.ITINERARY}?${itinerarySearch.toString()}`);
      } catch (error: unknown) {
        // Only log pnr_health_check or route_decision failures here
        logManageTripsEvent({
          ...getErrorTelemetry(error),
          durationMs: getDurationMs(telemetryStart),
          emailExists: emailExist,
          eventName: "qualifier_flow_failed",
          feature: "qualifier",
          flow: "qualifier",
          operation: failedOperation,
          phoneExists: phoneNumberExist,
          pnrState,
          resultRoute,
          status: "failure",
        });

        throw error;
      }
    },
    [
      emailExist,
      healthMutation,
      lastNameParameter,
      phoneNumberExist,
      platformOS,
      pnrParameter,
      router,
      search,
      svc,
    ],
  );

  const onSubmit = handleSubmit(async (raw: FormValues) => {
    setServerError(null);
    const signal = newAbortSignal();
    const telemetryStart = getTelemetryStart();

    const phone = raw.phone ?? "";
    if (isFormWithPhoneOnly && phone.trim() === "") {
      setError(
        "phone",
        { message: phoneMsgs.required, type: "required" },
        { shouldFocus: true },
      );
      return;
    }

    logManageTripsEvent({
      emailExists: emailExist,
      eventName: "qualifier_flow_started",
      feature: "qualifier",
      flow: "qualifier",
      operation: "qualifier_update",
      phoneExists: phoneNumberExist,
      status: "started",
    });

    try {
      let dob: string | undefined;

      if (!emailExist && raw.year && raw.month && raw.day) {
        dob = `${raw.year}-${raw.month}-${raw.day}`;
      }

      const lastName = lastNameParameter ?? "";
      const pnr = pnrParameter ?? "";

      const email = raw.email;
      const phoneNumber = raw.phone
        ? raw.phone.replaceAll(/\s/g, "")
        : undefined;

      const body: QualifierRequestBody = {
        mybQualifierRQ: {
          lastName,
          pnrRecordLocator: pnr,
          ...(dob ? { dob } : {}),
          ...(!emailExist && email ? { email } : {}),
          ...(phoneNumber ? { phoneNumber } : {}),
        },
      };

      // Only catch errors from the qualifier call
      try {
        await svc.mybPnrEmailQualifierService(body, signal);
      } catch (error: unknown) {
        const error_ = error as QualifierHttpError | { code?: string };

        logManageTripsEvent({
          ...getErrorTelemetry(error),
          durationMs: getDurationMs(telemetryStart),
          emailExists: emailExist,
          errorCode: error_.code,
          eventName: "qualifier_flow_failed",
          feature: "qualifier",
          flow: "qualifier",
          operation: "qualifier_update",
          phoneExists: phoneNumberExist,
          status: "failure",
        });

        setServerError(messageForCode(error_.code));
        return; // Don't proceed to afterQualifierFlow
      }

      // If qualifier succeeded, now run afterQualifierFlow
      await afterQualifierFlow({
        completionStatus: "success",
        telemetryStart,
      });
    } catch {
      // Don't log here! afterQualifierFlow already logs health check failures.
      setServerError(messageForCode());
    }
  });

  const onSubmitVoid = useCallback(() => {
    void onSubmit();
  }, [onSubmit]);

  const onSkip = useCallback(async () => {
    const telemetryStart = getTelemetryStart();

    logManageTripsEvent({
      emailExists: emailExist,
      eventName: "qualifier_flow_started",
      feature: "qualifier",
      flow: "qualifier",
      operation: "qualifier_update",
      phoneExists: phoneNumberExist,
      status: "skipped",
    });

    try {
      await afterQualifierFlow({
        completionStatus: "skipped",
        telemetryStart,
      });
    } catch {
      // Only afterQualifierFlow logs the failure event for skip
      setServerError(messageForCode());
    }
  }, [afterQualifierFlow, emailExist, phoneNumberExist]);

  const handleSkipClick = useCallback(() => {
    void onSkip();
  }, [onSkip]);

  const isHealthPending = healthMutation.isPending;

  const isBusy = isSubmitting || isPendingTransition || isHealthPending;

  if (guardInvalid) {
    return null;
  }

  const phoneLabel =
    labels.phone?.[0]?.label ??
    (isFormWithPhoneOnly ? "Phone" : "Phone - Optional");

  return (
    <form className="max-w-md space-y-4" noValidate onSubmit={onSubmitVoid}>
      {serverError && <div role="alert">{serverError}</div>}

      {!emailExist && (
        <>
          <div className="flex flex-col">
            <ClientInput
              autoComplete="email"
              label={labels.email?.label ?? "Traveler email"}
              name="email"
              onBlur={emailField.onBlur}
              onChange={emailField.onChange}
              type="email"
              value={emailField.value}
            />
            {emailState.error?.message && (
              <span
                style={{
                  color: "#d50f0f",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  marginTop: "0.25rem",
                }}
              >
                {emailState.error.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <div className="pb-2 text-l-body-extra-bold text-core-blue antialiased">
              {labels.dateOfBirth?.label ?? "Confirm Date of Birth"}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col">
                <ClientInput
                  inputMode="numeric"
                  label={labels.dateOfBirth?.monthLabel ?? "MM"}
                  name="month"
                  onBlur={() => {
                    onDobBlur("month");
                  }}
                  onChange={monthField.onChange}
                  value={monthField.value}
                />
                {monthState.error?.message && (
                  <span
                    style={{
                      color: "#d50f0f",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      marginTop: "0.25rem",
                    }}
                  >
                    {monthState.error.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <ClientInput
                  inputMode="numeric"
                  label={labels.dateOfBirth?.dayLabel ?? "DD"}
                  name="day"
                  onBlur={() => {
                    onDobBlur("day");
                  }}
                  onChange={dayField.onChange}
                  value={dayField.value}
                />
                {dayState.error?.message && (
                  <span
                    style={{
                      color: "#d50f0f",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      marginTop: "0.25rem",
                    }}
                  >
                    {dayState.error.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <ClientInput
                  inputMode="numeric"
                  label={labels.dateOfBirth?.yearLabel ?? "YYYY"}
                  name="year"
                  onBlur={() => {
                    onDobBlur("year");
                  }}
                  onChange={yearField.onChange}
                  value={yearField.value}
                />
                {yearState.error?.message && (
                  <span
                    style={{
                      color: "#d50f0f",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      marginTop: "0.25rem",
                    }}
                  >
                    {yearState.error.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {!phoneNumberExist && (
        <div className="flex flex-col">
          <ClientInput
            inputMode="tel"
            label={phoneLabel}
            name="phone"
            onBlur={onPhoneBlur}
            onChange={phoneField.onChange}
            value={phoneField.value}
          />
          {phoneState.error?.message && (
            <span
              style={{
                color: "#d50f0f",
                fontSize: "0.875rem",
                fontWeight: 600,
                marginTop: "0.25rem",
              }}
            >
              {phoneState.error.message}
            </span>
          )}
        </div>
      )}

      {isFormWithPhoneOnly && (
        <div>
          <Button
            className="mb-4 w-full md:w-auto"
            disabled={isBusy}
            onClick={handleSkipClick}
            size="large"
            type="button"
          >
            {labels.skipButton?.[0]?.label ?? "Skip"}
          </Button>
        </div>
      )}

      <div>
        <Button
          className="w-full md:w-auto"
          disabled={isBusy}
          size="large"
          type="submit"
        >
          {labels.submitCTA?.[0]?.label ?? "Verify"}
        </Button>
      </div>
    </form>
  );
}
